import { ContentLink } from "@components/cms/link/ContentLink";
import { H } from "@ecco/ui/a11y-heading";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import type { FC } from "react";
import { RichText } from "../rich-text-renderer/RichText";
import type { HeadlineModel } from "@lib/contentful/mappers/modules/headline/types";
import { Button } from "@ecco/ui/button";
import { headingSizeMap, mapSizesDescription, mapSizesTitle } from "@ecco/ui/map-sizes";
import { useContrastText } from "@ecco/ui/use-contrast-text";
import { eccoClubHorizontalGradient } from "@ecco/ui/tokens/colors";

const alignmentMap = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
} as const;

const contentMaxW = ["100%", "100%", "75%", "50%"];

type HeadlineProps = HeadlineModel & { isModule?: boolean };

export const Headline: FC<HeadlineProps> = ({
    title,
    titleSize = "xlarge",
    description,
    descriptionSize = "normal",
    textAlignment = "left",
    backgroundColor,
    isGradientBackground = false,
    callToActions = [],
    isModule = true,
}) => {
    const { isBlack } = useContrastText(backgroundColor);
    const titleColor = isBlack ? "black" : "white";
    const descriptionColor = isGradientBackground ? "white" : titleColor;

    return (
        <Flex
            w="full"
            flexDirection="column"
            textAlign={textAlignment}
            gap={3}
            alignItems={["flex-start", isModule ? alignmentMap[textAlignment] : "flex-start"]}
            px={[3, 4, 6]}
            py={[3, 4, 4]}
            pt={isModule ? 3 : [3, 8, 8]}
            background={isGradientBackground ? eccoClubHorizontalGradient : undefined}
            backgroundColor={isGradientBackground ? undefined : backgroundColor}
        >
            {title && (
                <Box maxW={contentMaxW}>
                    <H
                        as={headingSizeMap[titleSize]}
                        fontSize={mapSizesTitle[titleSize]}
                        w={["full", "full"]}
                        m="auto"
                        color={titleColor}
                    >
                        {title}
                    </H>
                </Box>
            )}
            {description && (
                <Box maxW={contentMaxW}>
                    <Box
                        textAlign={["start", "unset"]}
                        w="full"
                        m="auto"
                        sx={{
                            "& p": {
                                fontSize: mapSizesDescription[descriptionSize],
                            },
                        }}
                        color={descriptionColor}
                    >
                        <RichText data={description} />
                    </Box>
                </Box>
            )}
            {callToActions.length > 0 && (
                <Flex wrap="wrap" justifyContent="start" gap={2} w={["100%", "auto"]}>
                    {callToActions.map((button) => {
                        const isOutline = button.variant === "outline";
                        const gradientProps = isGradientBackground
                            ? {
                                  bg: isOutline ? "transparent" : "white",
                                  color: isOutline ? "white" : "black",
                                  borderColor: "white" as const,
                                  _hover: {
                                      textDecoration: "none",
                                      bg: isOutline ? "whiteAlpha.200" : "gray.100",
                                      color: isOutline ? "white" : "black",
                                  },
                              }
                            : {
                                  colorScheme: "black",
                                  _hover: { textDecoration: "none" },
                              };

                        return (
                            <Button
                                key={button.id}
                                rightIcon={<ECCOIcon name="arrow-right" />}
                                as={ContentLink}
                                link={button.link}
                                variant={button.variant}
                                textDecoration="none"
                                h={10}
                                w="auto"
                                {...gradientProps}
                            >
                                <span>{button.label}</span>
                            </Button>
                        );
                    })}
                </Flex>
            )}
        </Flex>
    );
};
